import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Wand2, GitBranch, Zap, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

const LABEL = {
  volume: "Volume", diversity: "Diversity", uniqueness: "Uniqueness",
  relevance: "Relevance", richness: "Richness",
};

export default function ImproveDialog({ crawlId, open, onOpenChange, onApplied }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !crawlId) return;
    setPlan(null);
    setLoading(true);
    api.improvePlan(crawlId)
      .then(setPlan)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not build a plan"))
      .finally(() => setLoading(false));
  }, [open, crawlId]);

  const apply = async (mode) => {
    setBusy(true);
    try {
      const r = await api.improveApply(crawlId, mode);
      toast.success(mode === "new_job"
        ? "New improved job created and started"
        : `Snapshot v${r.version} saved — job extended with the fixes`);
      onOpenChange(false);
      onApplied?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0B0F18] border-border" data-testid="improve-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-cyan-400" /> Improve this corpus
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Weak spots from the quality score, and exactly what I'd change to fix them.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 grid place-items-center text-slate-500 font-mono text-xs">
            <Loader2 className="h-5 w-5 animate-spin mb-2" /> analysing corpus…
          </div>
        )}

        {plan && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-md border border-border bg-[#0E1420] px-4 py-3">
              <span className="font-mono font-black text-2xl text-cyan-300 tabular-nums">{plan.score}</span>
              <div>
                <p className="font-mono text-xs text-slate-300">Grade {plan.grade}</p>
                <p className="text-[11px] text-slate-600 font-mono">top terms: {plan.top_terms?.slice(0, 5).join(", ")}</p>
              </div>
            </div>

            {plan.healthy ? (
              <div data-testid="improve-healthy" className="flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5" />
                <p className="text-xs text-emerald-300">
                  Nothing to fix — this corpus scores well on every component. Extend it if you just want more data.
                </p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="improve-items">
                {plan.items.map((it) => (
                  <div key={it.component} className="rounded-md border border-border bg-[#0E1420] p-3"
                    data-testid={`improve-item-${it.component}`}>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{LABEL[it.component]}</span>
                      <span className="font-mono text-[11px] text-rose-300 tabular-nums">{Math.round(it.score * 100)}/100</span>
                    </div>
                    <p className="text-xs text-slate-300 mt-1.5">{it.issue}</p>
                    <p className="text-[11px] text-cyan-300 mt-1 flex gap-1.5"><span className="text-cyan-600">›</span>{it.action}</p>
                  </div>
                ))}
              </div>
            )}

            {!plan.healthy && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-600 mb-1.5">Config changes</p>
                <pre data-testid="improve-changes"
                  className="terminal-scroll max-h-[160px] overflow-auto rounded-md border border-border bg-[#04060A] p-3 font-mono text-[11px] text-cyan-200/90">
                  {JSON.stringify(plan.changes, null, 2)}
                </pre>
              </div>
            )}

            <div className={cn("flex flex-wrap gap-3 pt-1", plan.healthy && "opacity-50 pointer-events-none")}>
              <Button data-testid="improve-extend-button" onClick={() => apply("extend")} disabled={busy}
                className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold">
                {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
                Apply &amp; extend this job
              </Button>
              <Button data-testid="improve-newjob-button" onClick={() => apply("new_job")} disabled={busy}
                variant="outline" className="border-border hover:bg-slate-800">
                <GitBranch className="h-4 w-4 mr-1.5" /> Create a new improved job
              </Button>
            </div>
            <p className="text-[11px] text-slate-600">
              Extending snapshots the dataset first, so you can always roll back from Dataset Versions.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
